<template>
  <div class="tier-card" :class="tierIndex === 0 ? 'tier-card--first' : 'tier-card--other'">

    <!-- Header strip -->
    <div class="tier-card__header">
      <div class="tier-card__header-left">
        <el-tag
          :type="tierIndex === 0 ? 'success' : 'info'"
          effect="light"
          size="small">
          Tier {{ tierIndex + 1 }}
        </el-tag>
        <span class="tier-card__hint">
          {{ tierIndex === 0 ? 'From handover (month 0)' : 'Starts when Tier ' + tierIndex + ' ends (month ' + tier.fromMonth + ')' }}
        </span>
      </div>
      <div class="tier-card__header-right">
        <el-tooltip v-if="tierIndex > 0" content="Remove tier" placement="top">
          <el-button
            type="text"
            :aria-label="'Remove Tier ' + (tierIndex + 1)"
            :disabled="disabled"
            @click="onRemove">
            <i class="fa fa-trash"></i>
          </el-button>
        </el-tooltip>
      </div>
    </div>

    <!-- Tenor Range -->
    <h5 class="tier-card__section-heading">Tenor Range (months)</h5>
    <div class="tier-card__slider-row">
      <div class="tier-card__slider-col">
        <el-slider
          ref="slider"
          :value="sliderValue"
          range
          show-stops
          :step="6"
          :min="0"
          :max="maxMonths"
          :marks="marks"
          :disabled="disabled"
          @input="onSliderInput"
          @change="onSliderChange">
        </el-slider>
        <!-- per-field validation -->
        <div v-if="fromMonthError" style="color:#f56c6c; font-size:12px; margin-top:4px;">
          {{ fromMonthError }}
        </div>
        <div v-if="rangeError" style="color:#f56c6c; font-size:12px; margin-top:4px;">
          {{ rangeError }}
        </div>
      </div>
      <div class="tier-card__slider-readout" :class="{ 'tier-card__slider-readout--error': rangeError }">
        <div class="tier-card__readout-range">
          From {{ sliderValue[0] }} mo &rarr; To {{ sliderValue[1] }} mo
        </div>
        <div class="tier-card__readout-years" :class="{ 'tier-card__readout-years--error': rangeError }">
          {{ rangeError ? '(invalid range)' : '(' + readoutYears + ' years)' }}
        </div>
      </div>
    </div>

    <!-- Rate inputs are el-form-item-based atoms (currency-input / number-input);
         they must live inside an <el-form> or el-form-item renders empty.
         Hidden in template mode — the Default Tier Template defines only the
         tier month ranges, not rate values. -->
    <el-form v-if="!templateMode" label-position="top">

      <!-- Sharer Rates grid. While a completeness error is shown (set on a
           blocked save attempt), each still-missing Rate input gets a red
           border — the border clears live as the user fills the field. -->
      <h5 class="tier-card__section-heading">Sharer Rates</h5>
      <el-row :gutter="12">
        <el-col
          v-for="i in rateCount"
          :key="i"
          :xs="24" :sm="12" :md="6" :lg="4"
          :class="{ 'tier-card__rate-missing': completenessError && !(Number(tier['telco' + i]) > 0) }">
          <currency-input
            :label="'Rate #' + i"
            :value="templateMode ? templateRateValue(tier['telco' + i]) : tier['telco' + i]"
            :placeholder="templateMode ? 'optional' : undefined"
            :disabled="disabled"
            @input="onRateInput('telco' + i, $event)" />
        </el-col>
      </el-row>
      <div v-if="completenessError" class="tier-card__completeness-error">
        {{ completenessError }}
      </div>

      <!-- MW & RRU row -->
      <h5 class="tier-card__section-heading">MW &amp; RRU</h5>
      <el-row :gutter="12">
        <el-col :span="8">
          <currency-input
            label="MW Rate"
            :value="templateMode ? templateRateValue(tier.mwRate) : tier.mwRate"
            :placeholder="templateMode ? 'optional' : undefined"
            :disabled="disabled"
            @input="onRateInput('mwRate', $event)" />
        </el-col>
        <el-col :span="8">
          <currency-input
            label="RRU Rate"
            :value="templateMode ? templateRateValue(tier.rruRate) : tier.rruRate"
            :placeholder="templateMode ? 'optional' : undefined"
            :disabled="disabled"
            @input="onRateInput('rruRate', $event)" />
        </el-col>
        <el-col :span="8">
          <number-input
            label="RRU Set"
            :min="0"
            :max="10"
            :value="templateMode ? templateRateValue(tier.rruSet) : tier.rruSet"
            :placeholder="templateMode ? 'optional' : undefined"
            :disabled="disabled"
            @input="onRateInput('rruSet', $event)" />
        </el-col>
      </el-row>

    </el-form>

  </div>
</template>

<script>
import NumberMixins from '@/mixins/NumberMixins'

export default {
  name: 'sharer-rate-tier-card',

  mixins: [NumberMixins],

  props: {
    tier: {
      type: Object,
      required: true
    },
    tierIndex: {
      type: Number,
      required: true
    },
    maxMonths: {
      type: Number,
      default: 300
    },
    // Parent rate's "To Sharer" — controls how many Rate #N inputs to show,
    // matching origin/main's `v-for="i in Number(row.toSharer)"`.
    toSharer: {
      type: [Number, String],
      default: 10
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Validation errors passed in from parent after submit
    fromMonthError: {
      type: String,
      default: null
    },
    rangeError: {
      type: String,
      default: null
    },
    completenessError: {
      type: String,
      default: null
    },
    // Template mode: render all 10 rate fields regardless of toSharer;
    // blank/NaN emitted as null instead of 0; null rendered as empty string.
    templateMode: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      // Local, mutable slider state so the handle can actually move while
      // dragging. A computed getter pinned to the props would let el-slider
      // reset itself on every drag tick (the original bug). Kept in sync with
      // the tier props via the watchers below for external changes (re-chain).
      sliderValue: [this.tier.fromMonth, this.tier.toMonth]
    }
  },

  watch: {
    'tier.fromMonth' (val) {
      if (this.sliderValue[0] !== val) this.sliderValue = [val, this.sliderValue[1]]
    },
    'tier.toMonth' (val) {
      if (this.sliderValue[1] !== val) this.sliderValue = [this.sliderValue[0], val]
    }
  },

  computed: {
    // How many Rate #N inputs to render.
    // In template mode always 10 (all sharers may use this template).
    // Otherwise the parent rate's "To Sharer" value (origin/main parity).
    // Clamped to 1..10 since tiers store telco1..telco10.
    rateCount () {
      if (this.templateMode) return 10
      var n = Number(this.toSharer)
      if (isNaN(n) || n < 1) return 1
      return Math.min(n, 10)
    },

    readoutYears () {
      var diff = this.sliderValue[1] - this.sliderValue[0]
      return this.formatNumber(diff / 12, 1)
    },

    marks () {
      return {
        24: '2 yrs',
        72: '6 yrs',
        120: '10 yrs',
        168: '14 yrs',
        216: '18 yrs',
        264: '22 yrs'
      }
    }
  },

  methods: {
    onSliderInput (val) {
      // Fires continuously while dragging AND once on a track-click. Lock the
      // left handle at the chained fromMonth (only the right handle is
      // user-editable) and reflect the new toMonth locally so the handle moves
      // and the readout updates live.
      this.sliderValue = [this.tier.fromMonth, val[1]]
      // A track-click can grab the left handle; force the slider to re-sync to
      // our locked value (it won't on its own — see syncLockedHandle).
      this.syncLockedHandle(false)
    },

    onSliderChange (val) {
      // Fires on release / after a track-click. The left handle is locked, so
      // we only honour the right handle (toMonth); fromMonth is emitted
      // unchanged.
      var toMonth = val[1]
      this.sliderValue = [this.tier.fromMonth, toMonth]
      this.$emit('update:tier', Object.assign({}, this.tier, { toMonth: toMonth }))
      this.syncLockedHandle(true)
    },

    syncLockedHandle (force) {
      // el-slider's value watcher skips re-syncing the handles when the new
      // value equals the old one element-wise (element-ui slider/src/main.vue).
      // So re-asserting our locked [fromMonth, toMonth] does NOT pull a
      // track-grabbed left handle back on its own. Call the slider's own
      // setValues() to force it. Skip mid-drag (unless forced on release) to
      // avoid fighting an active drag.
      var self = this
      this.$nextTick(function () {
        var slider = self.$refs.slider
        if (slider && typeof slider.setValues === 'function' && (force || !slider.dragging)) {
          slider.setValues()
        }
      })
    },

    // In template mode, render null as '' (empty input) so the placeholder shows.
    // Non-null values are returned as-is (currency-input handles its own formatting).
    templateRateValue (val) {
      if (val === null || val === undefined) return ''
      return val
    },

    // Intercept rate input events.
    // In template mode: blank/NaN → null (not 0).
    // In normal mode: pass through the value as-is (v-model path unchanged).
    onRateInput (field, val) {
      if (this.templateMode) {
        var parsed = parseFloat(val)
        var coerced = (val === '' || val === null || val === undefined || isNaN(parsed)) ? null : parsed
        this.$emit('update:tier', Object.assign({}, this.tier, { [field]: coerced }))
      } else {
        this.$emit('update:tier', Object.assign({}, this.tier, { [field]: val }))
      }
    },

    onRemove () {
      this.$emit('remove')
    }
  }
}
</script>

<style scoped>
.tier-card {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 16px 16px 16px 22px;
  margin-bottom: 16px;
  position: relative;
}

.tier-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 4px 0 0 4px;
}

.tier-card--first::before {
  background: #67c23a;
}

.tier-card--other::before {
  background: #909399;
}

.tier-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  min-height: 32px;
}

.tier-card__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.tier-card__hint {
  font-size: 13px;
  color: #909399;
}

.tier-card__section-heading {
  margin: 12px 0 8px;
  font-size: 14px;
  font-weight: 500;
}

/* XOL-376 (R1): per-tier completeness message. clear+width guard against the
   el-col floats — with a partially-filled last row the div would otherwise
   flow up beside the floated columns and wrap badly. */
.tier-card__completeness-error {
  clear: both;
  width: 100%;
  color: #f56c6c;
  font-size: 12px;
  margin: 0 0 8px;
}

/* Red border on each Rate input still missing while the completeness error is
   displayed (deep selector reaches the currency-input's inner el-input). */
.tier-card__rate-missing >>> .el-input__inner {
  border-color: #f56c6c;
}

.tier-card__slider-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 4px;
}

.tier-card__slider-col {
  flex: 3;
}

.tier-card__slider-readout {
  flex: 1;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 8px 12px;
}

.tier-card__slider-readout--error {
  border-color: #f56c6c;
}

.tier-card__readout-range {
  font-size: 14px;
  color: #303133;
}

.tier-card__readout-years {
  font-size: 12px;
  color: #909399;
}

.tier-card__readout-years--error {
  color: #f56c6c;
}
</style>
